import { useMemo } from 'react';
import { Anketa } from './anketa/Anketa';
import { ClientApp } from './app/ClientApp';
import { Privacy } from './app/Privacy';
import { Confirm, tokenFromPath } from './confirm/Confirm';
import { createAnketaApi } from './anketa/api';
import { createHubApi, kioskTestMode, withoutTestStaff } from './api/client';
import { createOperatorApi } from './operator/api';
import { Operator } from './operator/Operator';
import { createStaffApi } from './tablet/api';
import { Tablet } from './tablet/Tablet';
import { KioskV2 } from './v2/kiosk/KioskV2';
import { appMembership } from './v2/kiosk/membership';

/** One device = one location. Configured per device later (MDM / device registration). */
const params = new URLSearchParams(window.location.search);
const LOCATION_ID = params.get('location') ?? 'parede';
/**
 * Opened from the client app (owner 26.10.2026): `app=book|gift|checkin` starts the flow directly
 * (no kiosk splash, no language screen, no «Call staff»); `lang` is the app's language.
 */
const APP_START = ((v) => (v === 'book' || v === 'gift' || v === 'checkin' ? v : undefined))(params.get('app'));
const APP_LANG = ((v) =>
  ['pt', 'en', 'fr', 'es', 'ru'].includes(v ?? '') ? (v as 'pt' | 'en' | 'fr' | 'es' | 'ru') : undefined)(
  params.get('lang'),
);
/** Booking with a membership from the app (owner 26.10.2026): its name and the services it covers. */
const APP_MEMBERSHIP = appMembership(params);

/** Test mode is visible on screen, so a kiosk is never left in it by accident (owner 21.10.2026). */
const TEST_MODE = kioskTestMode();

export default function App() {
  return (
    <>
      <Routes />
      {TEST_MODE && (
        <div
          role="status"
          style={{
            position: 'fixed',
            left: 8,
            bottom: 8,
            zIndex: 100,
            padding: '4px 10px',
            borderRadius: 6,
            background: '#e51e26',
            color: '#fff',
            font: '600 13px system-ui, sans-serif',
            pointerEvents: 'none',
          }}
        >
          TESTE · Mila
        </div>
      )}
    </>
  );
}

function Routes() {
  const hubApi = useMemo(() => withoutTestStaff(createHubApi(), TEST_MODE), []);
  const staffApi = useMemo(() => createStaffApi(), []);
  const anketaApi = useMemo(() => createAnketaApi(), []);
  const operatorApi = useMemo(() => createOperatorApi(), []);
  const path = window.location.pathname;
  // Owner 22.10.2026: client app on the phone (PWA, added to the home screen) — hub.360face.pt/app
  if (path === '/app' || path.startsWith('/app/')) return <ClientApp />;
  // Privacy policy of the client app (App Store, owner 22.10.2026) — hub.360face.pt/privacy
  if (path.startsWith('/privacy')) return <Privacy />;
  // Owner 22.10.2026: the client confirms the visit from Hub's e-mail — hub.360face.pt/confirm/<token>
  if (path.startsWith('/confirm')) return <Confirm token={tokenFromPath(path)} />;
  // Owner 20.10.2026: remote operator console for kiosk video calls — hub.360face.pt/operator
  if (path.startsWith('/operator')) return <Operator api={operatorApi} />;
  // Owner 18.10.2026: separate client form for a studio iPad (not the kiosk) — hub.360face.pt/anketa
  if (path.startsWith('/anketa')) return <Anketa api={anketaApi} locationId={LOCATION_ID} />;
  // Owner 02.11.2026: versions 1 and 2 are removed (v1 lived at «/») — old links open the current kiosk (v3) and tablet.
  const moved = movedPath(path);
  if (moved) {
    window.location.replace(moved + window.location.search + window.location.hash);
    return null;
  }
  // Owner 23.10.2026: version 3 — the same kiosk in the style of the client-app mockup — /v3.
  if (path.startsWith('/v3'))
    return (
      <KioskV2
        api={hubApi}
        locationId={LOCATION_ID}
        v3
        app={
          APP_START && APP_START !== 'checkin'
            ? { start: APP_START, locale: APP_LANG ?? 'pt', ...(APP_MEMBERSHIP ? { membership: APP_MEMBERSHIP } : {}) }
            : undefined
        }
      />
    );
  // Owner 16.10.2026: link in the Altegio reminder — the client prepares today's visit on the phone.
  if (path.startsWith('/pre'))
    return (
      <KioskV2
        api={hubApi}
        locationId={LOCATION_ID}
        remote
        v3
        app={APP_START === 'checkin' ? { start: 'checkin', locale: APP_LANG ?? 'pt' } : undefined}
      />
    );
  if (path.startsWith('/tablet')) {
    return <Tablet staffApi={staffApi} hubApi={hubApi} locationId={LOCATION_ID} />;
  }
  // Anything else (an unknown or mistyped address) → the kiosk.
  window.location.replace('/v3' + window.location.search);
  return null;
}

/**
 * Old addresses of removed versions (owner 02.11.2026): «/», «/v2…» → «/v3»,
 * «/v2/tablet» → «/tablet». Undefined = the path is current.
 */
export function movedPath(path: string): string | undefined {
  if (path.startsWith('/v2/tablet')) return '/tablet';
  if (path === '/' || path === '' || path === '/index.html' || /^\/v2(\/|$)/.test(path)) return '/v3';
  return undefined;
}
